import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { CircleMarker, Popup, Tooltip } from 'react-leaflet';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';
import { driverRecordShape } from './driverRecordShape';
import {
  formatDeltaLabel,
  formatDriverNames,
} from '../../../utils/displayFormat';
import {
  isSeverelyLate,
  SEVERELY_LATE_MIN,
} from '../../../utils/composeDriverRecords';
import { DEFAULT_POLL_INTERVAL_MS } from '../../../hooks/useDriverLocationsPoll';
import legendStyles from './legendStyles';
import { toggleSetValue } from './monitoringFilters';
import MonitoringMapShell from './MonitoringMapShell';
import buildSingleRouteLayers from './buildSingleRouteLayers';
import { MIN_LINE_POINTS, hasCoords } from './routePaths';
import { SORT_SCHEDULED, stopDisplayNumbers } from './stopOrdering';
import CollapsibleSidePanel from './CollapsibleSidePanel';
import RouteStopsPanel from './RouteStopsPanel';
import StopPopupContent from './StopPopupContent';
import TrailHoverCard from './TrailHoverCard';
import PollCountdown from './PollCountdown';
import RouteForecastList from './RouteForecastList';
import Button from '../../../components/atoms/forms/Button';
import Badge from '../../../components/atoms/Badge';

// Single-route view: a collapsible Stops panel on the left and the map card
// on the right. The map draws the driven path in route blue, the predicted
// path ahead in dashed ink, and stop discs colored by state (green stocked,
// ink otherwise, soft red skipped); the panel tells the same stops' story
// row by row, numbered exactly like the discs. Entered from the list's route
// link, a pin popup, or a panel card; the back button returns to the view
// the route was opened from.
//
// The pane holds no state across routes: the parent keys it on the selected
// route, so a different route mounts a fresh pane with the default sort,
// nothing focused and nothing hovered.

const PANEL_STORAGE_KEY = 'monitoring:route-stops-panel-open';

// The legend's clickable stop-state filters: keys are display states (see
// stopDisplayState), swatches match the discs. An empty selection shows
// every stop marker; lines always draw.
const STOP_STATE_FILTERS = [
  { key: 'done', label: 'Stocked', dotClass: 'stocked' },
  { key: 'pending', label: 'Not stocked', dotClass: 'hollow' },
  { key: 'skipped', label: 'Skipped', dotClass: 'skipped' },
  { key: 'current', label: 'Current stop', dotClass: 'pulse' },
];

const StyledDiv = styledComponent('div', {
  display: 'grid',
  gridTemplateColumns: 'auto 1fr',
  gap: theme.spacing.sectionGap,
  alignItems: 'stretch',

  '& .rmp-map-card': {
    background: theme.colors.bgCard,
    borderRadius: theme.radii.card,
    border: theme.borders.card,
    boxShadow: theme.shadows.card,
    minHeight: theme.sizes.mapPaneMinHeight,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minWidth: 0,
  },

  '& .rmp-header': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    borderBottom: `1px solid ${theme.colors.borderLight}`,
  },
  '& .rmp-title-block': { minWidth: 0 },
  // Deliberately larger than a default secondary button: this is the only
  // way out of the single-route view, so it reads as primary navigation.
  '& .rmp-back': {
    marginBottom: theme.spacing.gapMd,
  },
  '& .rmp-title': {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing.gapSm,
    fontSize: theme.sizes.subheadFontSize,
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.textDark,
    marginTop: '2px',
  },
  '& .rmp-subtitle': {
    fontSize: theme.sizes.smallFontSize,
    color: theme.colors.textMuted,
    fontVariantNumeric: 'tabular-nums',
    marginTop: '2px',
  },
  '& .rmp-controls': {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },

  ...legendStyles('rmp'),
});

const NO_GPS_EXPLANATION =
  "The driver's phone reported no GPS positions this stocking day, so the map draws no driven path. Stop discs and times still come from scan and stocking events.";

function RouteMapPane({
  driver,
  lastPolledAt,
  isLoading,
  pollIntervalMs,
  initialFocusedStopId,
  onBack,
}) {
  // The Stops panel's sort pill; also drives the map's disc numbering.
  const [sortMode, setSortMode] = useState(SORT_SCHEDULED);
  // The recorded point under the cursor, or null — set by the trail's hover
  // handlers, read by the inspection card pinned over that point.
  const [hoverPoint, setHoverPoint] = useState(null);
  // The stop the camera is on and whose popup is open: the one the pane was
  // opened onto (a driver-card stop click), then whatever the panel clicks.
  const [focusedStopId, setFocusedStopId] = useState(initialFocusedStopId);
  // Legend filter: the stop display states whose markers stay on the map.
  const [stopStateFilter, setStopStateFilter] = useState(() => new Set());

  // Renumbering redraws the discs out from under the cursor.
  const handleSortModeChange = (nextMode) => {
    setSortMode(nextMode);
    setHoverPoint(null);
  };
  const handleSelectStop = (stop) => {
    setFocusedStopId(stop.stopId);
    setHoverPoint(null);
  };

  // Numbering covers ALL stops (not just mappable ones) so the panel rows and
  // the discs agree even when some stops carry no coordinates.
  const routeStops = driver.routeStops || [];
  const stopNumbers = useMemo(
    () => stopDisplayNumbers(routeStops, sortMode),
    [routeStops, sortMode],
  );

  // Memoized on the inputs that change the map, NOT on the hover: Leaflet
  // fires mousemove per pointer event, and rebuilding would hand react-leaflet
  // fresh positions, icons and dot centers to re-project on every one of them.
  // `setHoverPoint` is a stable setter, so it needs no dependency entry.
  const {
    polylines,
    stopMarkers,
    trailDots,
    gapMarkers,
    startMarker,
    driverMarker,
  } = useMemo(
    () =>
      buildSingleRouteLayers(driver, {
        stopNumbers,
        onTrailHover: setHoverPoint,
        stopStateFilter,
      }),
    [driver, stopNumbers, stopStateFilter],
  );
  const fitTargets = useMemo(() => [driver], [driver]);
  // Poll re-renders keep the same id, so the camera only moves on a NEW
  // click (see MonitoringMapShell's focus effect).
  const focusedStop = focusedStopId
    ? routeStops.find(
        (stop) => stop.stopId === focusedStopId && hasCoords(stop),
      ) || null
    : null;
  // Two recorded positions is the least that can draw a line — below that the
  // route gets the "No GPS reported" badge and no driven path at all.
  const hasGpsTrail = (driver.pingTrail || []).length >= MIN_LINE_POINTS;

  const deviation = formatDeltaLabel(driver.etaDeltaMin, { unit: 'min' });
  const severeNote = isSeverelyLate(driver.etaDeltaMin)
    ? ` · more than ${SEVERELY_LATE_MIN} min behind`
    : '';

  return (
    <StyledDiv>
      <CollapsibleSidePanel
        title="Stops"
        count={routeStops.length}
        storageKey={PANEL_STORAGE_KEY}
        side="left"
      >
        <RouteStopsPanel
          driver={driver}
          sortMode={sortMode}
          onSortModeChange={handleSortModeChange}
          onSelectStop={handleSelectStop}
        />
      </CollapsibleSidePanel>
      <div className="rmp-map-card">
        <div className="rmp-header">
          <div className="rmp-title-block">
            <div className="rmp-back">
              <Button
                variant="secondary-md"
                onClick={onBack}
                dataTestId="route-map-back"
              >
                ← Back
              </Button>
            </div>
            <div className="rmp-title">
              {driver.routeName}
              {hasGpsTrail ? null : (
                <span className="rmp-no-gps" title={NO_GPS_EXPLANATION}>
                  <Badge variant="attention">No GPS reported</Badge>
                </span>
              )}
            </div>
            <div className="rmp-subtitle">
              {`${formatDriverNames(
                driver.driverNames,
              )} · ${deviation}${severeNote}`}
            </div>
          </div>
          <div className="rmp-controls">
            <PollCountdown
              lastPolledAt={lastPolledAt}
              intervalMs={pollIntervalMs}
              isLoading={isLoading}
            />
          </div>
        </div>
        <div className="rmp-legend" aria-label="Map legend" role="group">
          <span className="rmp-legend-item">
            <span className="rmp-swatch-line" /> Driven
          </span>
          <span className="rmp-legend-item">
            <span className="rmp-swatch-line dashed" /> Predicted
          </span>
          <span className="rmp-legend-item">
            <span className="rmp-swatch-line faint" />
            <span className="rmp-swatch-diamond" /> GPS gap
          </span>
          <span className="rmp-legend-item">
            <span className="rmp-swatch-line other-driver" /> Stocked by another
            driver
          </span>
          {STOP_STATE_FILTERS.map((filter) => (
            <Button
              key={filter.key}
              variant="legend-pill"
              pressed={stopStateFilter.has(filter.key)}
              title={`Show only ${filter.label.toLowerCase()} markers`}
              onClick={() =>
                setStopStateFilter((current) =>
                  toggleSetValue(current, filter.key),
                )
              }
            >
              <span className={`rmp-swatch-dot ${filter.dotClass}`} />
              {filter.label}
            </Button>
          ))}
          <span className="rmp-legend-item">
            <svg
              className="rmp-swatch-chevron"
              viewBox="0 0 26 26"
              aria-hidden="true"
            >
              <path d="M13 3 L20.5 21.5 L13 16.5 L5.5 21.5 Z" />
            </svg>
            {` Driver more than ${SEVERELY_LATE_MIN} min behind`}
          </span>
        </div>
        <MonitoringMapShell
          fitTargets={fitTargets}
          fitKey={driver.routeId}
          focusPoint={
            focusedStop ? [focusedStop.latitude, focusedStop.longitude] : null
          }
        >
          {polylines}
          {trailDots}
          {gapMarkers}
          {startMarker}
          {stopMarkers}
          {hoverPoint ? (
            <CircleMarker
              center={[hoverPoint.latitude, hoverPoint.longitude]}
              radius={6}
              color={theme.colors.white}
              weight={2}
              fillColor={theme.colors.routeLine}
              fillOpacity={1}
              interactive={false}
            >
              <Tooltip permanent direction="top" offset={[0, -8]}>
                <TrailHoverCard driver={driver} point={hoverPoint} />
              </Tooltip>
            </CircleMarker>
          ) : null}
          {focusedStop ? (
            // Standalone popup (opens on mount) over the focused stop, so a
            // panel click lands with the stop's details already showing.
            // Closing it or opening any marker popup dismisses it.
            <Popup
              key={`focus-${focusedStop.stopId}`}
              position={[focusedStop.latitude, focusedStop.longitude]}
              offset={[0, -16]}
            >
              <StopPopupContent stop={focusedStop} driver={driver} />
            </Popup>
          ) : null}
          {driverMarker}
        </MonitoringMapShell>
        <RouteForecastList
          drivers={fitTargets}
          selectedRouteId={driver.routeId}
        />
      </div>
    </StyledDiv>
  );
}

RouteMapPane.propTypes = {
  driver: driverRecordShape.isRequired,
  lastPolledAt: PropTypes.instanceOf(Date),
  isLoading: PropTypes.bool,
  pollIntervalMs: PropTypes.number,
  // Stop to open the map on; read once at mount (the parent keys the pane on
  // the route, so a new route is a new mount).
  initialFocusedStopId: PropTypes.string,
  onBack: PropTypes.func.isRequired,
};

RouteMapPane.defaultProps = {
  lastPolledAt: null,
  isLoading: false,
  pollIntervalMs: DEFAULT_POLL_INTERVAL_MS,
  initialFocusedStopId: null,
};

export default RouteMapPane;
